import { ApolloError } from '@apollo/client'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { ContactProjectListPermissionsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

export interface ContactProjectListPermissions {
  loading: boolean
  error: ApolloError | undefined
  policies: {
    canCreateProjects: boolean
    canManageProjects: boolean
    canSeeCosts: boolean
    hasCostsFeature: boolean
    hasExpenses: boolean
    hasFixedTimeBudget: boolean
    hasTimeTrackingSection: boolean
    hasProjectsClient: boolean
  }
}

export const useContactProjectListPermissions = (): ContactProjectListPermissions => {
  const { company } = useCurrentSession()
  const {
    data: policies,
    loading,
    error,
  } = useQuery(ContactProjectListPermissionsDocument, {
    variables: {
      companyId: company.id,
      ids: [Features.projects_costs, Features.projects_budget],
      expensesModuleId: AvailableModules.expenses,
    },
  })

  const featureProjectCostsAvailability = policies?.companies.featureAvailabilities.find(
    (item) => item.id === Features.projects_costs
  )
  const featureFixedTimeBudget = policies?.companies.featureAvailabilities.find(
    (item) => item.id === Features.projects_budget
  )
  const featureProjectClients = policies?.companies.featureAvailabilities.find(
    (item) => item.id === Features.projects_clients
  )?.available
  const projectPolicies = policies?.projectManagement._policies

  const canManageProjects = !!projectPolicies?.projectManage?.can
  const canCreateProjects = !!projectPolicies?.projectCreate?.can
  const canSeeCosts = !!projectPolicies?.projectSeeCost.can

  const hasCostsFeature = !!featureProjectCostsAvailability?.available
  const hasFixedTimeBudget = !!featureFixedTimeBudget?.available
  const hasTimeTrackingSection =
    !!policies?.attendance._policies.sidebarSeeAttendanceCompanyTopLevelItem.can
  const hasExpenses = !!policies?.companies.moduleAvailability?.available
  const hasProjectsClient = !!featureProjectClients
  return {
    loading: loading,
    error,
    policies: {
      canCreateProjects,
      canManageProjects,
      canSeeCosts,
      hasFixedTimeBudget,
      hasCostsFeature,
      hasExpenses,
      hasTimeTrackingSection,
      hasProjectsClient,
    },
  }
}
